import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase/client';
import { useBranding } from '@/lib/branding/BrandingProvider';

export type SessionTypeFilter = 'interview' | 'online_assessment' | 'all';

export interface NeuraxInterview {
  id: string;
  status: string;
  session_type: string | null;
  position_title: string | null;
  company_name: string | null;
  duration_seconds: number | null;
  message_count: number | null;
  started_at: string | null;
  ended_at: string | null;
  interview_date: string | null;
  interview_notes: string | null;
  created_at: string;
  b2b_end_client_id: string | null;
  license_id: string | null;
  // One-time reschedule tracking: rescheduled_at NULL = reschedule still available.
  original_interview_date: string | null;
  rescheduled_at: string | null;
  end_client: { id: string; name: string; email: string } | null;
}

const SESSION_SELECT = `
  id,
  status,
  session_type,
  position_title,
  company_name,
  duration_seconds,
  message_count,
  started_at,
  ended_at,
  interview_date,
  interview_notes,
  created_at,
  b2b_end_client_id,
  license_id,
  original_interview_date,
  rescheduled_at
`;

async function fetchInterviews(
  companyId: string,
  filter: SessionTypeFilter
): Promise<NeuraxInterview[]> {
  // 1. Sessions — no cross-schema embed (PostgREST schema cache is unreliable here)
  let q = supabase
    .schema('neurax' as never)
    .from('interview_sessions' as never)
    .select(SESSION_SELECT)
    .eq('b2b_company_id', companyId);

  if (filter !== 'all') q = q.eq('session_type', filter);

  const { data: sessions, error } = await q.order('created_at', { ascending: false });
  if (error) throw new Error(`interview_sessions: ${error.message}`);

  const rows = (sessions ?? []) as unknown as Omit<NeuraxInterview, 'end_client'>[];
  if (rows.length === 0) return [];

  const sessionIds = rows.map((r) => r.id);
  const candidateIds = Array.from(
    new Set(rows.map((r) => r.b2b_end_client_id).filter((v): v is string => Boolean(v)))
  );

  // 2. Parallel fetch: candidates + transcripts + coaching conversations
  //    (we count coaching messages via a separate messages query keyed off conversation ids)
  const neurax = supabase.schema('neurax' as never);
  const [candidatesRes, transcriptsRes, conversationsRes] = await Promise.all([
    candidateIds.length > 0
      ? supabase.from('b2b_end_clients').select('id, name, email').in('id', candidateIds)
      : Promise.resolve({ data: [], error: null }),
    neurax
      .from('interview_transcripts' as never)
      .select('interview_session_id, total_segments')
      .in('interview_session_id', sessionIds),
    neurax
      .from('conversations' as never)
      .select('id, interview_session_id')
      .in('interview_session_id', sessionIds),
  ]);

  if (candidatesRes.error) throw new Error(`b2b_end_clients: ${candidatesRes.error.message}`);
  if (transcriptsRes.error)
    throw new Error(`interview_transcripts: ${transcriptsRes.error.message}`);
  if (conversationsRes.error) throw new Error(`conversations: ${conversationsRes.error.message}`);

  // Candidates map
  const candidatesById: Record<string, { id: string; name: string; email: string }> = {};
  for (const c of candidatesRes.data ?? []) {
    candidatesById[(c as { id: string }).id] = c as { id: string; name: string; email: string };
  }

  // Transcript segments per session
  const transcriptSegmentsBySession: Record<string, number> = {};
  for (const t of (transcriptsRes.data ?? []) as {
    interview_session_id: string;
    total_segments: number | null;
  }[]) {
    transcriptSegmentsBySession[t.interview_session_id] = t.total_segments ?? 0;
  }

  // Coaching messages: bulk-fetch messages for all conversations, group by session
  const conversations = (conversationsRes.data ?? []) as {
    id: string;
    interview_session_id: string;
  }[];
  const sessionByConversationId: Record<string, string> = {};
  for (const c of conversations) sessionByConversationId[c.id] = c.interview_session_id;

  const coachingCountBySession: Record<string, number> = {};
  if (conversations.length > 0) {
    const { data: msgs, error: msgErr } = await neurax
      .from('messages' as never)
      .select('id, conversation_id')
      .in(
        'conversation_id',
        conversations.map((c) => c.id)
      );
    if (msgErr) throw new Error(`messages: ${msgErr.message}`);
    for (const m of (msgs ?? []) as { conversation_id: string }[]) {
      const sid = sessionByConversationId[m.conversation_id];
      if (!sid) continue;
      coachingCountBySession[sid] = (coachingCountBySession[sid] || 0) + 1;
    }
  }

  // 3. Merge — override stale message_count + duration with live values
  const now = Date.now();
  return rows.map((r) => {
    const transcriptSegs = transcriptSegmentsBySession[r.id] ?? 0;
    const coachingMsgs = coachingCountBySession[r.id] ?? 0;
    const liveMessageCount = transcriptSegs + coachingMsgs;

    // Duration: prefer DB column; otherwise compute from started_at if active
    let liveDuration = r.duration_seconds;
    if (liveDuration == null && r.started_at) {
      const started = new Date(r.started_at).getTime();
      if (Number.isFinite(started)) {
        liveDuration = Math.max(0, Math.floor((now - started) / 1000));
      }
    }

    return {
      ...r,
      message_count: liveMessageCount > 0 ? liveMessageCount : (r.message_count ?? 0),
      duration_seconds: liveDuration,
      end_client: r.b2b_end_client_id ? (candidatesById[r.b2b_end_client_id] ?? null) : null,
    };
  }) as NeuraxInterview[];
}

export function useInterviews(filter: SessionTypeFilter = 'all') {
  const { companyId } = useBranding();
  return useQuery({
    queryKey: ['neurax', 'interviews', companyId, filter],
    queryFn: () => fetchInterviews(companyId, filter),
    staleTime: 30_000,
  });
}
